import { PaymentProvider, PaymentToken } from "@greenflagdate/shared";
import { ForwardRefExoticComponent, RefAttributes } from "react";

/**
 * What the user is told when nothing more specific is known — no provider copy
 * for the code, no category on the error, or no code at all.
 *
 * Lives here, in the leaf of the payments client, because both the shared copy
 * resolver and each adapter's own card UI need it and neither can import the
 * other without closing a cycle.
 */
export const GENERIC_PAYMENT_ERROR = "Unknown credit card processing error.";

export interface CardFieldsHandle {
  /**
   * Validate whatever the user entered and mint a token for it.
   *
   * Resolves null when the adapter has already surfaced a field-level error —
   * the caller stops silently rather than stacking a second message on top.
   *
   * `postalCode` comes back from the adapter rather than being read off shared
   * state because a provider may collect it inside its own UI, where we cannot
   * see it.
   */
  tokenize(): Promise<{ token: PaymentToken; postalCode: string } | null>;
}

export interface CardFieldsProps {
  /** Server-side failure copy, rendered inside the provider's own card UI. */
  error?: string;
  /** Rendered as a Cancel affordance when replacing an existing card. */
  onCancel?: () => void;
}

export interface PaymentClientAdapter {
  provider: PaymentProvider;

  /** Warm the provider SDK. Idempotent; resolves once tokenizing is safe. */
  preload(): Promise<void>;

  /**
   * The provider's card entry UI.
   *
   * It owns its own fields, its own validation, and its own tokenization.
   * That is the whole point: Authorize.net fills our CardFormLayout with text
   * inputs and calls Accept.js, while Stripe fills it with Elements iframes
   * whose card number never reaches our code. A "give me the card fields and
   * I will tokenize" interface cannot express the second one.
   */
  CardFields: ForwardRefExoticComponent<
    CardFieldsProps & RefAttributes<CardFieldsHandle>
  >;

  /** Copy for one of this provider's own codes, or null to fall back. */
  translateError(code?: string): string | null;
}
